'use client';

import type { SanityBuilding } from '@metmuseum/types';
import cn from 'classnames';
import SanityImage from '@sanity-image';
import LinkButton from 'components/controls/links/link-button';
import getBuildingUrl from 'lib/visit/getBuildingUrl';
import type { CSSProperties } from 'react';
import {
	MET_FIFTH_BUILDING_ID,
	CLOISTERS_BUILDING_ID,
} from 'lib/constants.mjs';
import styles from './building-banner.module.scss';

function getBuildingHighlightColor(buildingId: string) {
	if (buildingId === CLOISTERS_BUILDING_ID) {
		return 'var(--color-green-dark)';
	}

	if (buildingId === MET_FIFTH_BUILDING_ID) {
		return 'var(--color-met-red)';
	}

	return 'var(--color-met-red)';
}

function getAnalyticsPayload(buildingId: string) {
	const basePayload = {
		Content_Type: 'building',
		event: 'select_content',
	};

	if (buildingId === CLOISTERS_BUILDING_ID) {
		return {
			...basePayload,
			Content_Name: 'Looking for The Met Cloisters?',
			Content_Block: 'Cloisters Promo',
		};
	}

	if (buildingId === MET_FIFTH_BUILDING_ID) {
		return {
			...basePayload,
			Content_Name: 'Looking for The Met Fifth Avenue?',
			Content_Block: 'Met Fifth Promo',
		};
	}

	return null;
}

export default function BuildingBanner({ building }: BuildingBannerProps) {
	const {
		_id,
		image,
		globalPromoDescription: description,
		globalPromoCTA: cta,
	} = building;
	const link = getBuildingUrl({ _id, _type: 'building' });

	return (
		<div
			className={styles.container}
			style={
				{
					'--building-banner-background-color': getBuildingHighlightColor(_id),
				} as CSSProperties
			}
		>
			<div className={styles.imageContainer}>
				<SanityImage
					image={image}
					sizes="(max-width: 960px) 100vw, 50vw"
					fill={true}
				/>
			</div>
			<div className={styles.textContainer}>
				<p className={styles.description}>{description}</p>
				<LinkButton
					href={link}
					text={cta}
					className={cn('button', 'tertiary', styles.cta)}
					analytics={getAnalyticsPayload(_id) ?? undefined}
				/>
			</div>
		</div>
	);
}

export interface BuildingBannerProps {
	building: BuildingGlobalPromoData;
}

export type BuildingGlobalPromoData = Pick<
	SanityBuilding,
	'_id' | '_type' | 'image' | 'globalPromoDescription' | 'globalPromoCTA'
>;
